import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Zap,
  Play,
  CheckCircle,
  XCircle,
  Loader2,
  FileUp,
  ArrowRight,
} from 'lucide-react';
import { rootService, RootLogLevel } from '@/services/root.service';
import { useDeviceStore } from '@/stores/device.store';
import { cn } from '@/lib/utils';

interface RootWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StepStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

interface Step {
  id: number;
  title: string;
  description: string;
  status: StepStatus;
  log: string[];
}

const initialSteps: Step[] = [
  {
    id: 1,
    title: '准备 ksud 文件',
    description: '选择本地的 ksud 文件，或跳过此步骤（如果文件已存在于手机）',
    status: 'pending',
    log: [],
  },
  {
    id: 2,
    title: '推送文件到手机',
    description: '将 ksud 文件推送到 /data/local/tmp/ 并设置执行权限',
    status: 'pending',
    log: [],
  },
  {
    id: 3,
    title: '重启到 Fastboot',
    description: '将设备重启到 Fastboot 模式',
    status: 'pending',
    log: [],
  },
  {
    id: 4,
    title: 'Fastboot 操作',
    description: '使用 WebUSB 连接 Fastboot 设备，设置 SELinux 宽容模式并启动系统',
    status: 'pending',
    log: [],
  },
  {
    id: 5,
    title: '执行 Root',
    description: '获取 root 权限并验证结果',
    status: 'pending',
    log: [],
  },
];

export function RootWizardDialog({ open, onOpenChange }: RootWizardDialogProps) {
  const [steps, setSteps] = useState<Step[]>(initialSteps);
  const [currentStep, setCurrentStep] = useState(1);
  const [ksudFile, setKsudFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [showReconnectButton, setShowReconnectButton] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { adb } = useDeviceStore();

  // 自动滚动日志
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [steps]);

  const addLog = useCallback((stepId: number, message: string, level: RootLogLevel = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const prefix = {
      info: '',
      success: '[✓] ',
      error: '[✗] ',
      warning: '[!] ',
    }[level];

    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId
          ? { ...step, log: [...step.log, `${timestamp} ${prefix}${message}`] }
          : step
      )
    );
  }, []);

  const updateStepStatus = useCallback((stepId: number, status: StepStatus) => {
    setSteps((prev) =>
      prev.map((step) => (step.id === stepId ? { ...step, status } : step))
    );
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setKsudFile(file);
      addLog(1, `已选择文件: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`, 'success');
      updateStepStatus(1, 'completed');
    }
  };

  const handleStart = async () => {
    if (!adb) {
      addLog(1, '错误: 未连接到设备', 'error');
      return;
    }

    setIsRunning(true);

    try {
      // 步骤 2: 推送文件
      await executeStep2();

      // 步骤 3: 重启到 Fastboot
      await executeStep3();

      // 步骤 4: 初始化 Fastboot 步骤状态
      initStep4();

      // 步骤 4 需要用户手动点击按钮连接 Fastboot 设备
      // 步骤 5 需要用户手动点击按钮继续执行 Root
      // 这里不自动执行后续步骤

    } catch (error: any) {
      addLog(currentStep, `执行失败: ${error.message}`, 'error');
      updateStepStatus(currentStep, 'failed');
    } finally {
      setIsRunning(false);
    }
  };

  // 步骤 2: 推送文件到手机
  const executeStep2 = async () => {
    if (!adb) return;

    setCurrentStep(2);
    updateStepStatus(2, 'in_progress');
    addLog(2, '开始推送文件...', 'info');

    try {
      if (ksudFile) {
        const arrayBuffer = await ksudFile.arrayBuffer();
        const fileData = new Uint8Array(arrayBuffer);
        await rootService.pushKsudFile(adb, fileData, 'ksud-aarch64-linux-android');
      } else {
        addLog(2, '跳过文件推送（未选择文件）', 'warning');
      }

      // 设置权限
      await rootService.setFilePermissions(adb, '/data/local/tmp/ksud-aarch64-linux-android');
      updateStepStatus(2, 'completed');
      addLog(2, '文件推送和权限设置完成', 'success');
    } catch (error: any) {
      addLog(2, `失败: ${error.message}`, 'error');
      updateStepStatus(2, 'failed');
      throw error;
    }
  };

  // 步骤 3: 重启到 Fastboot
  const executeStep3 = async () => {
    if (!adb) return;

    setCurrentStep(3);
    updateStepStatus(3, 'in_progress');
    addLog(3, '正在重启到 Fastboot 模式...', 'info');

    try {
      await rootService.rebootToFastboot(adb);
      // 重启命令执行后设备会立即断开，这是正常的
      updateStepStatus(3, 'completed');
      addLog(3, '设备正在重启，请等待进入 Fastboot 模式', 'success');
    } catch (error: any) {
      // socket 断开是正常的，因为设备正在重启
      if (error.message?.includes('Socket') || error.message?.includes('stream')) {
        updateStepStatus(3, 'completed');
        addLog(3, '设备正在重启，请等待进入 Fastboot 模式', 'success');
      } else {
        addLog(3, `重启失败: ${error.message}`, 'error');
        updateStepStatus(3, 'failed');
      }
    }
  };

  // 步骤 4: 设置 Fastboot 状态
  const initStep4 = () => {
    setCurrentStep(4);
    updateStepStatus(4, 'in_progress');
    addLog(4, '设备正在重启到 Fastboot 模式...', 'info');
    addLog(4, '请等待设备进入 Fastboot 模式，然后点击下方按钮连接', 'warning');
  };

  // 连接 Fastboot 设备并执行命令
  const handleFastbootExecute = async () => {
    updateStepStatus(4, 'in_progress');
    addLog(4, '正在通过 WebUSB 连接 Fastboot 设备...', 'info');
    addLog(4, '请确保手机已进入 Fastboot 模式，并允许 USB 调试授权', 'info');

    try {
      const success = await rootService.executeFastbootCommand((msg) => {
        addLog(4, msg, 'info');
      });

      if (success) {
        updateStepStatus(4, 'completed');
        addLog(4, '✅ Fastboot 命令执行成功！', 'success');
        addLog(4, '设备正在启动，请等待系统启动后继续...', 'warning');
      } else {
        updateStepStatus(4, 'failed');
        addLog(4, '❌ Fastboot 操作失败', 'error');
      }
    } catch (error: any) {
      addLog(4, `❌ 连接失败: ${error.message}`, 'error');
      updateStepStatus(4, 'failed');
    }
  };

  // 步骤 5: 执行 Root
  const executeStep5 = async () => {
    setCurrentStep(5);
    updateStepStatus(5, 'in_progress');
    addLog(5, '设备正在启动，准备重新连接...', 'info');

    // 等待一段时间让设备启动
    await rootService.waitForAdbReconnect(
      (seconds) => {
        setWaitSeconds(seconds);
        addLog(5, `等待设备启动... ${seconds}秒`, 'info');
      },
      null, // 不传自动重连回调
      30 // 等待30秒后显示重连按钮
    );

    // 显示手动重连按钮
    setShowReconnectButton(true);
    addLog(5, '⚠️ 设备已启动，请点击下方"重新连接设备"按钮', 'warning');
    updateStepStatus(5, 'pending');
  };

  // 手动重新连接并继续执行 Root
  const handleReconnectAndContinue = async () => {
    setShowReconnectButton(false);
    updateStepStatus(5, 'in_progress');

    try {
      // 用户点击按钮触发，这是有效的用户手势
      const { connect } = useDeviceStore.getState();
      addLog(5, '正在连接设备，请在弹出窗口中选择设备...', 'info');

      await connect();
      await new Promise(resolve => setTimeout(resolve, 2000));

      const newAdb = useDeviceStore.getState().adb;
      if (!newAdb) {
        addLog(5, '❌ 连接失败，请重试', 'error');
        setShowReconnectButton(true);
        updateStepStatus(5, 'pending');
        return;
      }

      // 设备已连接，继续执行 Root
      addLog(5, '✓ 设备已连接，正在执行 Root...', 'success');

      // 执行 root 漏洞
      addLog(5, '正在执行 Root 漏洞...', 'info');
      await rootService.executeRootExploit(newAdb);
      addLog(5, '✓ Root 漏洞已执行', 'success');

      // 执行最终脚本
      addLog(5, '正在执行 Root 脚本...', 'info');
      await rootService.executeRootFinalScript(newAdb);
      addLog(5, '✓ Root 脚本已执行', 'success');

      // 验证结果
      const result = await rootService.verifyRootStatus(newAdb);
      if (result.isRooted) {
        addLog(5, `🎉 Root 验证成功！\n${result.details}`, 'success');
      } else {
        addLog(5, `Root 验证结果: ${result.details}`, 'warning');
      }

      updateStepStatus(5, 'completed');
    } catch (error: any) {
      addLog(5, `❌ Root 执行失败: ${error.message}`, 'error');
      updateStepStatus(5, 'failed');
    }
  };

  const handleClose = () => {
    if (!isRunning) {
      setSteps(initialSteps);
      setCurrentStep(1);
      setKsudFile(null);
      setWaitSeconds(0);
      setShowReconnectButton(false);
      onOpenChange(false);
    }
  };

  const getStepIcon = (step: Step) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const canExecuteFastboot = currentStep === 4 && (steps[3].status === 'in_progress' || steps[3].status === 'pending');
  const canStartStep5 = steps[3].status === 'completed' && currentStep >= 4;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            小米手机一键 Root 向导
          </DialogTitle>
          <DialogDescription>
            按照步骤指引完成 Root 权限获取，此操作会重启设备多次
          </DialogDescription>
        </DialogHeader>

        {/* 步骤指示器 */}
        <div className="flex items-center justify-between py-4 px-2">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={cn(
                  'flex flex-col items-center cursor-pointer',
                  step.id === currentStep && 'ring-2 ring-blue-500 rounded-lg p-1'
                )}
                onClick={() => !isRunning && step.status !== 'pending' && setCurrentStep(step.id)}
              >
                {getStepIcon(step)}
                <span className="text-xs mt-1 hidden sm:inline">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-8 sm:w-16 h-0.5 mx-1',
                    step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* 当前步骤内容 */}
        <div className="border rounded-lg p-4 my-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">
              步骤 {currentStep}: {steps[currentStep - 1]?.title}
            </h3>
            <Badge
              variant={
                steps[currentStep - 1]?.status === 'completed'
                  ? 'secondary'
                  : steps[currentStep - 1]?.status === 'failed'
                  ? 'destructive'
                  : 'outline'
              }
            >
              {steps[currentStep - 1]?.status === 'in_progress'
                ? '进行中'
                : steps[currentStep - 1]?.status === 'completed'
                ? '完成'
                : steps[currentStep - 1]?.status === 'failed'
                ? '失败'
                : '待开始'}
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            {steps[currentStep - 1]?.description}
          </p>

          {/* 步骤 1: 文件选择 */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label
                  htmlFor="ksud-file"
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-muted"
                >
                  <FileUp className="w-4 h-4" />
                  选择 ksud 文件
                </label>
                <input
                  id="ksud-file"
                  type="file"
                  accept=".bin,.any"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isRunning}
                />
                {ksudFile && (
                  <span className="text-sm text-green-600">
                    已选择: {ksudFile.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                如果不选择文件，将假设 ksud 文件已存在于手机 /data/local/tmp/ 目录
              </p>
            </div>
          )}

          {/* 步骤 4: Fastboot 按钮 */}
          {canExecuteFastboot && (
            <Button onClick={handleFastbootExecute} className="w-full bg-blue-600 hover:bg-blue-700">
              <Zap className="w-4 h-4 mr-2" />
              连接 Fastboot 设备并执行命令
            </Button>
          )}

          {/* 等待信息 */}
          {currentStep === 4 && steps[3].status === 'completed' && (
            <div className="text-sm text-green-600">
              Fastboot 操作已完成，正在等待设备启动...
            </div>
          )}

          {/* 步骤 5: 等待设备启动进度条 */}
          {currentStep === 5 && steps[4].status === 'in_progress' && waitSeconds > 0 && (
            <div className="space-y-2">
              <Progress value={(waitSeconds / 30) * 100} />
              <p className="text-sm text-center text-muted-foreground">
                等待设备启动: {waitSeconds}/30 秒
              </p>
            </div>
          )}

          {/* 步骤 5: 手动重连按钮 */}
          {currentStep === 5 && showReconnectButton && (
            <Button
              onClick={handleReconnectAndContinue}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Zap className="w-4 h-4 mr-2" />
              重新连接设备并继续 Root
            </Button>
          )}
        </div>

        {/* 日志区域 */}
        <div className="border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-sm font-medium">执行日志</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setSteps((prev) => prev.map((s) => ({ ...s, log: [] })))
              }
            >
              清除
            </Button>
          </div>
          <ScrollArea className="h-40">
            <div ref={scrollRef} className="p-3 font-mono text-xs space-y-1">
              {steps
                .flatMap((s) => s.log)
                .slice(-50)
                .map((log, index) => (
                  <div
                    key={index}
                    className={cn(
                      'whitespace-pre-wrap',
                      log.includes('[✓]') && 'text-green-600',
                      log.includes('[✗]') && 'text-red-600',
                      log.includes('[!]') && 'text-yellow-600'
                    )}
                  >
                    {log}
                  </div>
                ))}
              {steps.every((s) => s.log.length === 0) && (
                <span className="text-muted-foreground">等待执行...</span>
              )}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRunning}>
            {isRunning ? '执行中...' : '关闭'}
          </Button>

          {currentStep === 1 && (
            <Button onClick={handleStart} disabled={!adb || isRunning}>
              <Play className="w-4 h-4 mr-2" />
              开始执行
            </Button>
          )}

{(canStartStep5 || (currentStep === 5 && steps[4].status === 'pending')) && (
    <Button
      onClick={executeStep5}
      disabled={isRunning}
      className="bg-green-600 hover:bg-green-700"
    >
      <ArrowRight className="w-4 h-4 mr-2" />
      {currentStep === 5 && steps[4].status === 'pending' ? '重新连接并执行 Root' : '继续执行 Root'}
    </Button>
  )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}